import { Category } from '~/models/Category.models';
import type { OrderReceiptItemModel } from '~/models/order/OrderReceiptItem.api.model';
import type { ShoppingCartItemModel } from '~/models/shopping_cart/ShoppingCartItem.api.model';
import type { EcommerceItem } from '~/models/tracking/Events.model';

export const mapOrderReceiptItemModelToEcommerceItem = (
  item: OrderReceiptItemModel,
): EcommerceItem[] => {
  const ga4Items: EcommerceItem[] = [];

  if (item.Category === Category.phones) {
    ga4Items.push({
      down_pay_time: item.SelectedPayment?.DownPayments,
      item_brand: item.ManufacturerName,
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId ?? 0,
      item_name: _createHardwareProductItemName(item),
      item_variant: `${item.StorageSize?.DataSize || ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.ColorName || ''}`,
      price:
        item.PaymentOverDownPayTimePeriod ||
        item.SelectedPayment?.ProductDownPayment,
      quantity: 1,
    });

    if (item.BundlePlanItem?.Id) {
      ga4Items.push({
        item_brand: 'Halebop',
        item_category: 'Abonnemang',
        item_id: item.BundlePlanItem.ProductAttributeCombinationId ?? 0,
        item_name: item.BundlePlanItem.ProductName,
        item_variant:
          item.BundlePlanItem.BindingPeriod === 0 ? '0 månader' : '24 månader',
        price: item.BundlePlanItem.MonthlyPrice,
        quantity: 1,
      });
    }

    if (
      item.BundlePlanItem?.FamilyPlanChildPlanItems &&
      item.BundlePlanItem?.FamilyPlanChildPlanItems?.length > 0
    ) {
      item.BundlePlanItem.FamilyPlanChildPlanItems.forEach((familyPlanItem) => {
        ga4Items.push({
          item_brand: 'Halebop',
          item_category: familyPlanItem.Category,
          item_id: familyPlanItem.ProductAttributeCombinationId ?? 0,
          item_name: familyPlanItem.ProductName,
          item_variant: familyPlanItem.BindingPeriod + ' månader',
          price: familyPlanItem.MonthlyPrice,
          quantity: 1,
        });
      });
    }
  } else if (
    item.Category === Category.postPaid ||
    item.Category === Category.prePaid ||
    item.Category === Category.family
  ) {
    ga4Items.push({
      item_brand: 'Halebop',
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId ?? 0,
      item_name: item.ProductName,
      item_variant: item.BindingPeriod + ' månader',
      price:
        item.Category === Category.prePaid
          ? item.OneTimePrice
          : item.MonthlyPrice,
      quantity: 1,
    });
  } else if (item.Category === Category.insurances) {
    ga4Items.push({
      item_brand: 'Halebop',
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId ?? 0,
      item_name: 'Mobilförsäkring',
      item_variant: item.ProductName,
      price: item.MonthlyPrice,
      quantity: 1,
    });
  } else {
    ga4Items.push({
      item_brand: item.ManufacturerName,
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId,
      item_name: _createHardwareProductItemName(item),
      item_variant: `${item.StorageSize?.DataSize ? item.StorageSize?.DataSize : ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.ColorName ?? ''}`,
      price:
        item.PaymentOverDownPayTimePeriod ||
        item.SelectedPayment?.ProductDownPayment,
      quantity: 1,
    });
  }

  return ga4Items;
};

export const mapCartItemModelToEcommerceItem = (
  item: ShoppingCartItemModel,
  selectedDownPayTime?: number,
): EcommerceItem[] => {
  const ga4Items: EcommerceItem[] = [];

  if (item.Category === Category.phones) {
    ga4Items.push({
      down_pay_time: selectedDownPayTime,
      item_brand: item.ManufacturerName,
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId ?? 0,
      item_name: item.ProductName,
      item_variant: `${item.StorageSize?.DataSize ? item.StorageSize?.DataSize : ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.Color ?? ''}`,
      price: item.Payments?.find((payment) => payment.DownPayments === 1)
        ?.ProductDownPayment,
      quantity: 1,
    });

    if (item.BundlePlanItem && item.BundlePlanItem.Id) {
      ga4Items.push({
        item_brand: 'Halebop',
        item_category: 'Abonnemang',
        item_id: item.BundlePlanItem.ProductAttributeCombinationId ?? 0,
        item_name: item.BundlePlanItem.ProductName,
        item_variant:
          item.BundlePlanItem.BindingPeriod === 0 ? '0 månader' : '24 månader',
        price: item.BundlePlanItem.MonthlyPrice,
        quantity: 1,
      });
    }
  } else if (
    item.Category === Category.postPaid ||
    item.Category === Category.prePaid ||
    item.Category === Category.family
  ) {
    ga4Items.push({
      item_brand: 'Halebop',
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId ?? 0,
      item_name: item.ProductName,
      item_variant: item.BindingPeriod + ' månader',
      price:
        item.Category === Category.prePaid
          ? item.OneTimePrice
          : item.MonthlyPrice,
      quantity: 1,
    });
  } else if (item.Category === Category.insurances) {
    ga4Items.push({
      item_brand: 'Halebop',
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId ?? 0,
      item_name: 'Mobilförsäkring',
      item_variant: item.ProductName,
      price: item.MonthlyPrice,
      quantity: 1,
    });
  } else {
    ga4Items.push({
      down_pay_time: selectedDownPayTime,
      item_brand: item.ManufacturerName,
      item_category: item.Category,
      item_id: item.ProductAttributeCombinationId,
      item_name: item.ProductName,
      item_variant: `${item.StorageSize?.DataSize ? item.StorageSize?.DataSize : ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.Color ?? ''}`,
      price: item.Payments?.find((payment) => payment.DownPayments === 1)
        ?.ProductDownPayment,
      quantity: 1,
    });
  }

  return ga4Items;
};

function _createHardwareProductItemName(
  item: OrderReceiptItemModel,
): string | undefined {
  let productName = item.ProductName || '';
  const color = item.ColorName || '';
  const dataAllowanceString =
    item.StorageSize?.DataSize && item.StorageSize?.DataUnit
      ? `${item.StorageSize.DataSize}${item.StorageSize.DataUnit}`
      : '';

  if (color && productName.endsWith(color)) {
    productName = productName.slice(0, -color.length).trim();
  }

  if (dataAllowanceString && productName.endsWith(dataAllowanceString)) {
    productName = productName.slice(0, -dataAllowanceString.length).trim();
  }

  return productName;
}
